
import { connect } from 'react-redux';
import React, {useEffect,useRef,useState} from 'react'
import styled from 'styled-components'

import {dynamicSort} from '../../util/dynamicSort';
import AdminPageEdit from '../AdminPageEdit';
import Button from '../Button';
import ContentPaneBlock from '../ContentPaneBlock'
import PageList from '../PageList';
import SubPageList from '../SubPageList';
import Title from '../Title';
import TopPageLink from '../TopPageLink';

const Wrapper = styled.div`
  box-sizing: border-box;
  & p > br {
    display: none;
  }
`

const EditBtnWrapper = styled.div`
  padding: 0 1rem;
`

const ContentBlockWrapper = styled.div`
  padding: 1rem;
  max-width: 1000px;
  margin: 0 auto;

  /* The header is sticky, so scrollIntoView would otherwise tuck each block
     underneath it. This offset is measured from the live header and written to
     --content_pane_scroll_offset by the component, since the header grows with
     wrapped titles and with the safe area inset. */
  > div {
    scroll-margin-top: var(--content_pane_scroll_offset, 8rem);
  }
`
const BlockSelectWrapper = styled.div`
  padding-right: var(--content_pane_side_margin);
  padding-left: calc(env(safe-area-inset-left) + var(--content_pane_side_margin));
  padding-bottom: .4rem;
  padding-top: .4rem;
  max-width: 80%;
  @media(min-width: 800px) {
    padding-right: calc(var(--sidebar_width)  + var(--content_pane_side_margin));
    padding-left: calc(env(safe-area-inset-left) + var(--content_pane_side_margin));
  }
`

const BlockSelect = styled.select`
  font-size: .9rem;
  font-family: inherit;
  color: var(--gray-dark);
  background-color: var(--lavender-light);
  border: 1px solid var(--lavender);
  border-radius: 4px;
  padding: .2rem .4rem .3rem;
  padding-right: 2.5rem;
  margin: 0;
  max-width: 90%;
  font-weight: 600;

  @media (hover: hover) {
    &:hover {
      background-color: var(--light-orange);
    }
  }
`

const Header = styled.div`
  background: var(--lavender-very-light);
  position: sticky;
  top: 50px;
  left: 0;
  top: calc(env(safe-area-inset-top) + var(--mobile_header_height_with_padding));
  padding-top: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--burgundy-dark);
  min-height: var(--content_pane_header);
  width: 100%;
  margin: 0 auto;
  @media (min-width: 800px) {
    top: calc(env(safe-area-inset-top) + var(--toolbar_height));
  }
`

const HeaderInner = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`

const SubpageList = styled.div`
  margin: 2rem 0 0;
  border-top: 1px solid #aaa;
  padding-top: 1rem;
`

// Set off as its own section, holding a solid purple header bar. Measure and
// padding match ContentBlockWrapper so it lines up with the content above. The
// bottom padding matches TopPageLinkWrapper, so both footers end the same distance
// from the end of the document.
const SubpageLinks = styled.div`
  max-width: 1000px;
  margin: 2rem auto 0;
  padding: 0 1rem 1.5rem;
  background: var(--lavender-very-light);
  border: 1px solid var(--lavender-light);
`

// The same purple bar as the toolbar and the mobile header. The negative side
// margins run it out to the panel edges, while the panel's own padding still sets
// the measure that the links below are aligned to.
const SubpageLinksTitle = styled.h2`
  margin: 0 -1rem 0.4rem;
  padding: 0.4rem 1rem;
  background: var(--burgundy-dark);
  color: var(--off-white);
  font-size: 0.95rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`

// The way back up to the section, ruled off from the content the same way the
// admin subpage list is. The bottom padding keeps the last link off the end of the
// document; ContentWrapper clears the fixed toolbar below that.
const TopPageLinkWrapper = styled.div`
  max-width: 1000px;
  margin: 2rem auto 0;
  padding: 0.8rem 1rem 1.5rem;
  border-top: 1px solid var(--lavender);
`

const TitleDiv = styled.div`
  display: flex;
  justify-content: space-between;
`

const ContentTitle = styled.h1`
  font-size: 1.4rem;
  padding-left: calc(env(safe-area-inset-left) + var(--content_pane_side_margin));
  margin: 0;
  flex-grow: 2;
`

const ContentPane = props => {

  const [editMode, setEdit] = useState(false)
  const toggleEdit = () => setEdit(!editMode)

  const [currentContentBlock, setContentBlock] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);

  const headerRef = useRef(null);
  const selectRef = useRef(null);
  // Armed by a ?block= deep link, dropped the moment the reader scrolls or picks
  // a block themselves so the correction below can never move them again.
  const userScrolledRef = useRef(false);

  const blockChange = (event) => {
    const idx = Number(event.target.value);
    userScrolledRef.current = true;
    setContentBlock(idx);
    const el = document.getElementById(`content-block-${idx}`);
    if (el && el.scrollIntoView) el.scrollIntoView({behavior: 'smooth', block: 'start'});
  }

  const {id, navLevel = 0, page, contentBlocks, blockIndex = null, verified} = props

  const contentBlocksArray = contentBlocks ? Object.keys(contentBlocks).map((key) => {
    return { index: Number(key), header: contentBlocks[key].header, contentHTML:contentBlocks[key].contentHTML}
  }).sort(dynamicSort('index')) : false

  // Identity for dependency arrays, so the effects below re-run when the block
  // count changes without re-running on every unrelated render of the array.
  const blockCount = contentBlocksArray ? contentBlocksArray.length : 0;

  // A ?block= past the end of the page is a bad link, not a crash: it falls back
  // to the page top exactly the way a missing or non-numeric value does.
  const targetBlock = (blockIndex !== null && blockIndex >= 0 && blockIndex < blockCount) ? blockIndex : null;

  // A top level page is the manual section, so it gets the list of its own
  // subpages under the content. Subpages are reachable from the sidebar, but
  // that is a drawer on phones. A subpage gets the way back up instead.
  const subpages = page.subpages ? page.subpages : [];
  const showSubpageLinks = !page.parentId && subpages.length > 0;
  const showTopPageLink = !!page.parentId;

  useEffect(()=>{
    setEdit(false) //If the nav changes then go back to view mode and return to first content block
    setContentBlock(targetBlock === null ? 0 : targetBlock)
    if (targetBlock === null && window.scrollTo) window.scrollTo(0, 0)
  },[id,targetBlock,setEdit,setContentBlock])

  // Measure the sticky header so scrollIntoView can clear it. It has to be
  // measured rather than read from --content_pane_header, because that is only a
  // min-height: long titles wrap, and the safe area inset shifts the whole thing.
  useEffect(() => {
    const header = headerRef.current;
    if (!header || typeof ResizeObserver === 'undefined') return;

    const measure = () => {
      const height = header.getBoundingClientRect().height;
      const top = parseFloat(getComputedStyle(header).top) || 0;
      // getBoundingClientRect excludes borders, and the header keeps a 2px
      // bottom border plus the gap it sits below the mobile header.
      const border = parseFloat(getComputedStyle(header).borderBottomWidth) || 0;
      setHeaderHeight(Math.ceil(height + top + border));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  },[editMode, verified, blockCount])

  useEffect(() => {
    if (headerHeight) document.documentElement.style.setProperty('--content_pane_scroll_offset', `${headerHeight}px`);
  },[headerHeight])

  // Keep the dropdown pointing at the block the reader is actually looking at.
  // Debounced, and a click or programmatic scroll is ignored briefly so the
  // smooth scroll from the dropdown does not fight the listener.
  useEffect(() => {
    if (editMode || blockCount < 2) return;

    const nodes = [];
    for (let i = 0; i < blockCount; i++) {
      const node = document.getElementById(`content-block-${i}`);
      if (node) nodes.push(node);
    }
    if (!nodes.length) return;

    let lock = false;
    let timer = null;

    const pick = () => {
      if (lock) return;
      // The top of the viewport plus a little past the sticky header, so a block
      // only becomes current once its heading clears the chrome.
      const line = (headerHeight || 0) + 24;
      let active = 0;
      nodes.forEach((node, index) => {
        if (node.getBoundingClientRect().top <= line) active = index;
      });
      // Sitting at the very bottom should select the last block, even if it is
      // too short to ever reach the line.
      const atBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 2;
      setContentBlock(atBottom ? nodes.length - 1 : active);
    };

    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(pick, 100);
    };

    const release = () => { lock = false; }
    const engage = () => { lock = true; setTimeout(release, 700); }

    const select = selectRef.current;
    if (select) select.addEventListener('change', engage);

    window.addEventListener('scroll', onScroll, {passive: true});
    window.addEventListener('resize', onScroll);
    pick();

    return () => {
      clearTimeout(timer);
      if (select) select.removeEventListener('change', engage);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  },[editMode, blockCount, headerHeight])

  // Re-arm on navigation. Deliberately separate from the jump below so that a
  // headerHeight settle does not hand the reader back a correction they had
  // already outrun.
  useEffect(() => {
    userScrolledRef.current = false;
  },[id,targetBlock])

  useEffect(() => {
    const flag = () => { userScrolledRef.current = true; }
    window.addEventListener('wheel', flag, {passive: true});
    window.addEventListener('touchmove', flag, {passive: true});
    window.addEventListener('keydown', flag);
    return () => {
      window.removeEventListener('wheel', flag);
      window.removeEventListener('touchmove', flag);
      window.removeEventListener('keydown', flag);
    };
  },[])

  // The deep link. It reads the live header rather than the measured CSS
  // variable, because on a cold load that variable is not written until the
  // measure effect's follow-up render and a link based on it lands short. It
  // also has to sit after the scroll-spy above, which otherwise resets the
  // dropdown to whatever block happens to be under the viewport first.
  useEffect(() => {
    if (targetBlock === null || userScrolledRef.current) return;
    const el = document.getElementById(`content-block-${targetBlock}`);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - headerHeight;
    window.scrollTo(0, Math.max(0, top));
    setContentBlock(targetBlock);
  },[id,targetBlock,headerHeight,setContentBlock])

  return editMode ? (
    <Wrapper>
      <AdminPageEdit
            submitCallback={toggleEdit}
            id={id}
            parentId={page.parentId}
            title={page.title}
            contentBlocks={page.contentBlocks}
             />
      {!page.parentId ? <SubpageList><Title>SubPages</Title><PageList pagelist={page.subpages} parentId={id} navLevel={navLevel + 1} /> </SubpageList> : null}
    </Wrapper>
  ) : (
    <Wrapper>
      <Header ref={headerRef}>
        <HeaderInner>
        <TitleDiv>
          <ContentTitle>{page.title}</ContentTitle>
          {(verified === 'admin') ? <EditBtnWrapper><Button fontSize='0.8rem' onClick={toggleEdit} >Edit</Button></EditBtnWrapper> : null}
        </TitleDiv>
        {(contentBlocksArray && contentBlocksArray.length > 1) ?
          <BlockSelectWrapper>
            <BlockSelect ref={selectRef} aria-label='Content section' value={currentContentBlock} onChange={blockChange}>
              {contentBlocksArray.map( (contentBlock, index) => <option key={index} value={index}>{(contentBlock.header) ? contentBlock.header : 'Introduction'}</option> )}
            </BlockSelect>
          </BlockSelectWrapper>
        : null}
        </HeaderInner>
      </Header>
      {(contentBlocksArray) ? <ContentBlockWrapper>
        {contentBlocksArray.map( (contentBlock, index) => (
          <div key={index} id={`content-block-${index}`}>
            <ContentPaneBlock header={contentBlock.header} contentHTML={contentBlock.contentHTML} first={index === 0} />
          </div>
        ))}
      </ContentBlockWrapper> : null}
      {showSubpageLinks ?
        <SubpageLinks>
          <SubpageLinksTitle>{page.title}</SubpageLinksTitle>
          <SubPageList pagelist={subpages} />
        </SubpageLinks>
        : null}
      {showTopPageLink ? <TopPageLinkWrapper><TopPageLink id={id} /></TopPageLinkWrapper> : null}
    </Wrapper>
  )
}

const mapStateToProps = ({pages, auth}, ownProps) => {
  return {
      page: pages.pages[ownProps.id],
      contentBlocks: pages.pages[ownProps.id].contentBlocks,
      verified: auth.verified
  }
}

export default connect(mapStateToProps)(ContentPane)